import { CHECKBOX_STATES } from '../ThreeStatesCheckBox';
import Presenter from '../../../sharedKernel/mvp/Presenter';
import { LocationCountry, Location } from '../Location.Object';

const { UNCHECKED } = CHECKBOX_STATES;

export const ACTION = {
  TOGGLE_COUNTRY: 'TOGGLE_COUNTRY',
  TOGGLE_REGION: 'TOGGLE_REGION'
};

export default class LocationsDropdownPresenter extends Presenter {
  constructor() {
    super({
      viewModel: {
        state: null
      },
      onDependencyChange: changes => {
        /**
         * Locations changed because of a filter change, we need to get the previously checked locations.
         */
        if (changes.locations?.prev) {
          const previouslyCheckedLocations = this.getCheckedLocationsAsString(true);
          this.createState(changes.locations.next);
          previouslyCheckedLocations.forEach(l => this.dispatch(l));
        }
        /**
         * Create instances of LocationCountry for each country.
         */
        else if (changes.locations?.next) {
          this.createState(changes.locations.next);
        }
        /**
         * Some checkboxes have already been checked in the form / user profile preferences.
         */
        if (changes.alreadyCheckedLocations?.next) {
          this.viewModel()?.state.forEach(l => l.setUnchecked());

          if (changes.alreadyCheckedLocations?.next?.length !== 0) {
            changes.alreadyCheckedLocations.next.forEach(l => this.dispatch(l));
          }
        }
      }
    });
  }

  createState(locations) {
    const state = locations.map(l => new LocationCountry(l));

    /**
     * Put "remote" as the first item in the list.
     */
    const remoteIndex = state.findIndex(l => l.getSlug() === 'remote');
    if (remoteIndex !== -1) {
      state.splice(0, 0, state.splice(remoteIndex, 1)[0]);
    }

    this.update({state} );
  }

  /**
   * @return {string[]}
   */
  getCheckedLocationsAsString(withSlug = false) {
    return this.viewModel().state.reduce((regionNames, currentCountry) => {
      if (currentCountry.hasNoRegions() && currentCountry.isChecked()) {
        return regionNames.concat([withSlug ? currentCountry.getSlug() : currentCountry.getName()]);
      }
      return regionNames.concat(currentCountry.getCheckedLocations().map(r => withSlug ? r.getSlug() : r.getName()));
    }, []);
  }

  /**
   * @param {string} country
   * @param {string} region
   * @return {string}
   */
  getCheckboxStatus(country, region = undefined) {
    if (region) {
      return (
        this.viewModel()
          .state.find(l => l.slug === country)
          .regions.find(r => r.slug === region).checked || UNCHECKED
      );
    }

    return this.viewModel().state.find(l => l.slug === country).checked || UNCHECKED;
  }

  /**
   * @param {import('../type').LocationCountryOrRegion} location
   * @returns {string}
   */
  getTranslatedKey(location) {
    if (location.isOtherCountry()) {
      return location.isCountry() ? location.getSlug() : location.getName();
    }

    if (location.isCountry() && location.hasNoRegions()) {
      return `${location.getName()}`;
    }

    return `${location.getSlug()}`;
  }

  getCheckedLocations() {
    return this.viewModel().state.reduce((checkedLocations, locationCountry) => {
      if (locationCountry.isChecked()) {
        checkedLocations.push(locationCountry);
      } else {
        checkedLocations = checkedLocations.concat(...locationCountry.getCheckedLocations());
      }

      return checkedLocations;
    }, []);
  }

  /**
   * @param {string} locationSlug
   * @param {ACTION.TOGGLE_COUNTRY | ACTION.TOGGLE_REGION} type
   */
  dispatch(locationSlug, type = null) {
    if (!type) {
      type = this._isCountryWithNoRegion(locationSlug) ? ACTION.TOGGLE_COUNTRY : ACTION.TOGGLE_REGION;
    }

    this._reduce({
      type,
      payload: type === ACTION.TOGGLE_REGION ? this._payloadForRegion(locationSlug) : this._payloadForCountry(locationSlug)
    });
  }

  /**
   * @param locationSlug
   * @return {{country: *, region: unknown}}
   * @private
   */
  _payloadForCountry(locationSlug) {
    const country = this.viewModel().state.find(l => l.getSlug() === locationSlug);

    return { country, region: null };
  }

  /**
   * @param locationSlug
   * @return {{country: *, region: *}}
   * @private
   */
  _payloadForRegion(locationSlug) {
    const region = this.viewModel()
      .state.flatMap(l => l.regions)
      .find(r => r.getSlug() === locationSlug);

    if (!region) {
      return { country: null, region: null };
    }

    const country = this.viewModel().state.find(l =>
      l.regions.filter(r => r.country === region.country).length > 0
    );

    return { country, region };
  }

  /**
   * @param {import('./type').Action} action
   * @private
   */
  _reduce(action) {
    const { country, region } = action.payload;

    switch (action.type) {
      case ACTION.TOGGLE_REGION:
        region?.toggle();
        break;
      case ACTION.TOGGLE_COUNTRY:
        country?.toggle();
        break;
      default:
        throw Error('Action [' + action.type + '] not recognized.');
    }

    this.update({ state: this._withCountriesState(this.viewModel().state) });
  }

  /**
   * For each country, we need to update its state based on the regions' states.
   *
   * @param {import('../type').LocationCountry[]} state
   * @returns {import('../type').LocationCountry[]}
   * @private
   */
  _withCountriesState(state) {
    /**
     * @type {import('../type').LocationCountry} country
     */
    for (const country of state) {
      /**
       * We do not need to update the country if it has no regions.
       */
      if (country.hasNoRegions()) continue;

      if (country.hasAllRegionsUnchecked()) {
        country.setUnchecked();
      } else if (country.hasAllRegionsChecked()) {
        country.setChecked();
      } else {
        country.setIndeterminate();
      }
    }

    return state;
  }

  /**
   * @param {string} locationSlug
   * @returns {boolean}
   * @private
   */
  _isCountryWithNoRegion(locationSlug) {
    if (Location.isRelatedToAnOtherCountry(locationSlug)) {
      return true;
    }

    return (
      this.viewModel().state.find(
        l => l.getSlug() === locationSlug && l.regions.filter(r => r.name === '').length > 0
      ) !== undefined
    );
  }
}
